import type { Message } from '../types';
import { useAuthStore } from '../stores/useAuthStore';
import { useChannelStore } from '../stores/useChannelStore';
import { useChannelMemberStore } from '../stores/useChannelMemberStore';
import { useDmStore } from '../stores/useDmStore';
import { useThreadStore } from '../stores/useThreadStore';
import { useMessageStore } from '../stores/useMessageStore';
import * as api from './api';
import { createMessageCache, prepareCachedWindow, type CachedWindow, type ConversationKey, type MessageCache } from './message-cache';
import { appendReceipt, createReceiptBuffer, reconcileMessageWindow, type MessageReceipt, type ReceiptBuffer } from './message-cache-reconcile';

interface Account { userId: string; cache: MessageCache; enabled: boolean }
interface Owner { key: ConversationKey; parentChannelId?: string }
interface Read {
  channelId: string;
  account: Account | null;
  userId: string | null;
  owner: Owner | null;
  generation: number;
  loadId: string;
  controller: AbortController;
  head: AbortController;
  receipts: ReceiptBuffer;
  arrivals: Set<string>;
  preview: Set<string>;
  pending: boolean;
  previewStarted?: boolean;
  seed?: { messages: Message[]; hasMoreOlder: boolean; fetchedAt: number };
  correction: boolean;
  networkOnly: boolean;
  overflowed: boolean;
  resync: boolean;
  held: ReadonlySet<string>;
  correctionTimer?: ReturnType<typeof setTimeout>;
  correctionStarted?: number;
}
interface Canonical { read: Read; window: CachedWindow; timer?: ReturnType<typeof setTimeout>; queuedAt?: number }

let account: Account | null = null;
let generation = 0;
let nextLoad = 0;
let ownerSubscriptions: Array<() => void> = [];
const reads = new Map<string, Read>();
const canonical = new Map<string, Canonical>();
const visible = new Map<string, number>();
const publications = new Map<string, { rows: Message[]; read: Read; kind: 'cache' | 'server' }>();
const warned = new Set<string>();
const markHistory: string[] = [];

const mark = (read: Read, event: string): void => {
  if (typeof performance?.mark !== 'function') return;
  const name = `message-cache:${read.loadId}:${event}`;
  performance.mark(name);
  markHistory.push(name);
  if (markHistory.length > 120) performance.clearMarks(markHistory.shift()!);
};
const warn = (category: string): void => {
  if (warned.has(category)) return;
  warned.add(category);
  console.warn('[message-cache]', { component: 'message-cache', category });
};
const current = (read: Read): boolean => reads.get(read.channelId) === read &&
  read.generation === generation && read.userId === (useAuthStore.getState().currentUser?.id ?? null) &&
  (!read.account || read.account === account);

const resolveOwner = (channelId: string): Owner | null => {
  const dm = useDmStore.getState().dmChannels.some((item) => item.id === channelId);
  if (dm) return { key: ['dm', channelId] };
  const channels = useChannelStore.getState().channels;
  const channel = channels.find((item) => item.id === channelId);
  if (channel?.guild_id) return { key: ['server', channel.guild_id, channelId] };
  const parentId = useChannelMemberStore.getState().threadChannelsById[channelId]?.parent_id ??
    Object.values(useThreadStore.getState().byMessage).find((thread) => thread.id === channelId)?.parent_channel_id;
  if (!parentId) return null;
  if (useDmStore.getState().dmChannels.some((item) => item.id === parentId)) {
    return { key: ['dm', channelId], parentChannelId: parentId };
  }
  const parent = channels.find((item) => item.id === parentId);
  return parent?.guild_id ? { key: ['server', parent.guild_id, channelId], parentChannelId: parentId } : null;
};

const cancelCanonical = (channelId: string): void => {
  const entry = canonical.get(channelId);
  if (entry?.timer) clearTimeout(entry.timer);
  if (entry?.read.owner) entry.read.account?.cache.cancelWrites(entry.read.owner.key);
  canonical.delete(channelId);
};
const evict = (read: Read): void => {
  cancelCanonical(read.channelId);
  if (read.owner) {
    read.account?.cache.cancelWrites(read.owner.key);
    void read.account?.cache.removeWindows([read.channelId]);
  }
};
const flush = (entry: Canonical): void => {
  if (entry.timer) clearTimeout(entry.timer);
  entry.timer = undefined;
  entry.queuedAt = undefined;
  if (!current(entry.read) || !entry.read.account?.enabled || canonical.get(entry.read.channelId) !== entry) return;
  const snapshot = entry.window;
  void entry.read.account?.cache.writeWindow(snapshot, () =>
    current(entry.read) && canonical.get(entry.read.channelId) === entry && entry.window === snapshot);
};
const scheduleWrite = (entry: Canonical, priority = false): void => {
  if (entry.timer) clearTimeout(entry.timer);
  entry.queuedAt ??= Date.now();
  if (priority) { flush(entry); return; }
  entry.timer = setTimeout(() => flush(entry), Math.min(250, Math.max(0, 1000 - (Date.now() - entry.queuedAt))));
};

export const bindMessageCacheAccount = (userId: string): void => {
  if (account?.userId === userId) return;
  closeMessageCacheAccount();
  if (!['http:', 'https:'].includes(window.location.protocol)) return;
  const bound: Account = { userId, enabled: true, cache: createMessageCache({
    apiOrigin: window.location.origin, userId,
    onInvalidated: (reason) => {
      if (account !== bound) return;
      if (reason === 'purge') {
        closeMessageCacheAccount();
        useMessageStore.getState().reset();
      } else disableAccountStorage(bound);
    },
  }) };
  account = bound;
  ownerSubscriptions = [useChannelStore, useDmStore, useThreadStore, useChannelMemberStore]
    .map((store) => store.subscribe(() => refreshOwners()));
};

export const resetMessageCacheContext = (): void => {
  generation++;
  for (const read of reads.values()) {
    read.controller.abort(); read.head.abort();
    if (read.correctionTimer) clearTimeout(read.correctionTimer);
  }
  for (const id of canonical.keys()) cancelCanonical(id);
  reads.clear(); publications.clear();
};

export const closeMessageCacheAccount = (): void => {
  const previous = account;
  account = null;
  for (const unsubscribe of ownerSubscriptions) unsubscribe();
  ownerSubscriptions = [];
  resetMessageCacheContext();
  previous?.cache.close();
};

const disableAccountStorage = (bound: Account): void => {
  bound.enabled = false;
  for (const read of reads.values()) {
    if (read.account !== bound) continue;
    read.networkOnly = true;
    read.head.abort();
    cancelCanonical(read.channelId);
  }
};

export const captureMessageCachePurge = (): (() => void) => {
  const captured = account;
  let purged = false;
  return () => {
    if (purged) return;
    purged = true;
    if (captured && account === captured) closeMessageCacheAccount();
    void captured?.cache.clearAccount();
  };
};

export const rejectMessageViewer = (expected: string, actual: string): void => {
  if (expected === actual) return;
  closeMessageCacheAccount();
  useMessageStore.getState().reset();
  window.dispatchEvent(new CustomEvent('message-viewer-mismatch'));
};

export const endMessageCacheSession = (): void => {
  closeMessageCacheAccount();
  useMessageStore.getState().reset();
  window.dispatchEvent(new CustomEvent('message-auth-ended'));
};

export const disconnectMessageCache = (): void => {
  for (const read of reads.values()) {
    read.networkOnly = true;
    cancelCanonical(read.channelId);
  }
};

export const removeMessageCacheWindows = (channelIds: readonly string[]): string[] => {
  const removed = new Set(channelIds);
  const children = [
    ...Object.values(useThreadStore.getState().byMessage).map((thread) => [thread.id, thread.parent_channel_id]),
    ...Object.values(useChannelMemberStore.getState().threadChannelsById).map((thread) => [thread.id, thread.parent_id]),
    ...[...reads].map(([id, read]) => [id, read.owner?.parentChannelId]),
  ];
  for (let changed = true; changed;) {
    changed = false;
    for (const [id, parent] of children) if (id && parent && removed.has(parent) && !removed.has(id)) {
      removed.add(id); changed = true;
    }
  }
  for (const [id, read] of reads) {
    if (!removed.has(id) && !(read.owner?.parentChannelId && removed.has(read.owner.parentChannelId))) continue;
    read.controller.abort(); read.head.abort();
    if (read.correctionTimer) clearTimeout(read.correctionTimer);
    cancelCanonical(id); reads.delete(id); publications.delete(id);
  }
  void account?.cache.removeWindows([...removed]);
  return [...removed];
};

export const observeMessageConversation = (channelId: string): (() => void) => {
  visible.set(channelId, (visible.get(channelId) ?? 0) + 1);
  return () => {
    const count = (visible.get(channelId) ?? 1) - 1;
    if (count) visible.set(channelId, count); else visible.delete(channelId);
  };
};

export const markMessagePaint = (channelId: string, rows: Message[]): void => {
  const publication = publications.get(channelId);
  if (!publication || publication.rows !== rows) return;
  publications.delete(channelId);
  requestAnimationFrame(() => requestAnimationFrame(() => {
    if (current(publication.read)) mark(publication.read, `${publication.kind}-paint`);
  }));
};

const discardPreview = (read: Read): void => {
  if (!read.preview.size) return;
  const state = useMessageStore.getState();
  const rows = (state.messages[read.channelId] ?? []).filter((message) =>
    !read.preview.has(message.id) || read.arrivals.has(message.id));
  useMessageStore.setState({ messages: { ...state.messages, [read.channelId]: rows } });
  read.preview.clear();
};

const correction = (read: Read): void => {
  if (read.correction || !current(read) || !visible.has(read.channelId)) return;
  if (read.correctionTimer) clearTimeout(read.correctionTimer);
  read.correctionStarted ??= Date.now();
  read.correctionTimer = setTimeout(() => {
    read.correctionTimer = undefined;
    if (!current(read) || !visible.has(read.channelId)) return;
    void loadMessageWindow(read.channelId, {
      resync: read.resync, held: new Set((useMessageStore.getState().messages[read.channelId] ?? []).map((message) => message.id)), correction: true, networkOnly: read.networkOnly, overflowed: read.overflowed,
    }).catch(() => warn('correction-failed'));
  }, Math.min(250, Math.max(0, 1000 - (Date.now() - read.correctionStarted))));
};

/** Arrival identity is distinct from a row restored from disk, even under clock skew. */
export const noteMessageArrival = (channelId: string, messageId: string): void => {
  const read = reads.get(channelId);
  if (!read || !current(read)) return;
  read.preview.delete(messageId);
  if (read.pending && read.arrivals.size < 512) read.arrivals.add(messageId);
};

export const recordMessageReceipt = (channelId: string, receipt: MessageReceipt): void => {
  const targets = channelId ? [reads.get(channelId)].filter((read): read is Read => !!read) : [...reads.values()];
  for (const read of targets) {
    if (!current(read)) continue;
    const id = 'message' in receipt ? receipt.message.id : receipt.messageId;
    if (receipt.type !== 'reactions') read.preview.delete(id);
    if (read.pending) {
      const wasOverflowed = read.receipts.overflowed;
      appendReceipt(read.receipts, receipt);
      if (!wasOverflowed && read.receipts.overflowed) {
        read.networkOnly = true;
        read.overflowed = true;
        read.head.abort();
        evict(read);
      }
      continue;
    }
    if (read.correctionTimer) correction(read);
    const entry = canonical.get(read.channelId);
    if (read.networkOnly) continue;
    if (!entry) {
      if (read.seed) {
        if (receipt.type === 'ack' && !read.seed.messages.some((message) => message.id === id)) continue;
        const result = reconcileMessageWindow(read.seed.messages, [receipt]);
        if ((receipt.type === 'new' || receipt.type === 'ack') && result.ambiguous) { read.seed = undefined; correction(read); }
        else seedWindow(read, result.canonicalMessages, read.seed.hasMoreOlder, read.seed.fetchedAt);
      }
      continue;
    }
    if ((!channelId || receipt.type === 'ack') && !entry.window.messages.some((message) => message.id === id)) continue;
    const result = reconcileMessageWindow(entry.window.messages, [receipt]);
    // Creation/ACK snapshots cannot establish which conflicting mutation won.
    if ((receipt.type === 'new' || receipt.type === 'ack') && result.ambiguous) {
      evict(read); correction(read); continue;
    }
    const prepared = prepareCachedWindow({ ...entry.window, messages: result.canonicalMessages });
    if (!prepared) {
      entry.window = { ...entry.window, messages: [], hasMoreOlder: entry.window.hasMoreOlder || result.canonicalMessages.length > 0, utf8Bytes: 0 };
      if (entry.timer) clearTimeout(entry.timer);
      if (read.owner) read.account?.cache.cancelWrites(read.owner.key);
      void read.account?.cache.removeWindows([read.channelId]);
      continue;
    }
    entry.window = prepared;
    if (receipt.type === 'delete' && read.owner) read.account?.cache.cancelWrites(read.owner.key);
    scheduleWrite(entry, receipt.type === 'delete');
  }
};

const seedWindow = (read: Read, messages: Message[], hasMoreOlder: boolean, fetchedAt: number): void => {
  if (!current(read) || read.networkOnly || !read.account?.enabled) return;
  read.owner ??= resolveOwner(read.channelId);
  if (!read.owner) {
    read.seed = { messages: messages.slice(-100), hasMoreOlder: hasMoreOlder || messages.length > 100, fetchedAt };
    return;
  }
  read.seed = undefined;
  const window = prepareCachedWindow({ ...read.owner, messages, hasMoreOlder, fetchedAt }) ?? {
    ...read.owner, messages: [], hasMoreOlder: hasMoreOlder || messages.length > 0,
    fetchedAt, lastUsedAt: fetchedAt, utf8Bytes: 0,
  };
  const entry: Canonical = { read, window };
  canonical.set(read.channelId, entry);
  if (window.messages.length) scheduleWrite(entry);
  else void read.account.cache.removeWindows([read.channelId]);
};

const refreshOwners = (): void => {
  for (const read of reads.values()) {
    if (!current(read) || read.owner) continue;
    read.owner = resolveOwner(read.channelId);
    if (!read.owner) continue;
    if (read.seed) seedWindow(read, read.seed.messages, read.seed.hasMoreOlder, read.seed.fetchedAt);
    if (read.pending) hydrateFromCache(read);
  }
};

const hydrateFromCache = (read: Read): void => {
  const bound = read.account;
  const owner = read.owner;
  const channelId = read.channelId;
  if (bound?.enabled && owner && !read.networkOnly && read.pending && !read.previewStarted &&
    !useMessageStore.getState().messages[channelId]) {
    read.previewStarted = true;
    void bound.cache.readWindow(owner.key).then(async (cached) => {
      if (!cached?.messages.length || !current(read) || !bound.enabled || !read.pending || read.networkOnly ||
        useMessageStore.getState().messages[channelId] !== undefined) return;
      mark(read, 'head-start');
      try {
        const viewer = await api.authorizeMessagePreview(channelId, read.head.signal);
        if (!current(read) || !bound.enabled || !read.pending || read.networkOnly) return;
        if (viewer === null) { read.networkOnly = true; warn('missing-viewer'); return; }
        if (viewer !== read.userId) { rejectMessageViewer(read.userId!, viewer); return; }
        if (useMessageStore.getState().messages[channelId] !== undefined) return;
        mark(read, 'head-authorized');
        const rows: Message[] = cached.messages;
        read.preview = new Set(rows.map((message) => message.id));
        useMessageStore.setState((state) => ({
          messages: { ...state.messages, [channelId]: rows },
          hasMore: { ...state.hasMore, [channelId]: cached.hasMoreOlder },
        }));
        publications.set(channelId, { rows, read, kind: 'cache' });
      } catch (error) {
        if (!current(read) || read.head.signal.aborted) return;
        if (error instanceof api.ApiError && error.status === 401) { endMessageCacheSession(); return; }
        if (error instanceof api.ApiError && (error.status === 403 || error.status === 404)) {
          useMessageStore.getState().purgeChannels([read.channelId]);
        }
      }
    }).catch(() => warn('read-failed'));
  }
};

interface ReadOptions { resync?: boolean; held?: ReadonlySet<string>; correction?: boolean; networkOnly?: boolean; overflowed?: boolean }

export const loadMessageWindow = async (channelId: string, options: ReadOptions = {}): Promise<Message[]> => {
  const previous = reads.get(channelId);
  previous?.controller.abort(); previous?.head.abort();
  if (previous?.correctionTimer) clearTimeout(previous.correctionTimer);
  cancelCanonical(channelId);
  const userId = useAuthStore.getState().currentUser?.id ?? null;
  const owner = resolveOwner(channelId);
  const bound = account?.userId === userId ? account : null;
  const read: Read = {
    channelId, account: bound, userId, owner, generation, loadId: String(++nextLoad),
    controller: new AbortController(), head: new AbortController(), receipts: createReceiptBuffer(),
    arrivals: new Set(), preview: new Set(previous?.preview), pending: true,
    correction: options.correction === true, networkOnly: options.networkOnly === true, overflowed: options.overflowed === true,
    resync: options.resync === true, held: options.held ?? new Set(),
  };
  reads.set(channelId, read);
  if (owner) bound?.cache.cancelWrites(owner.key);
  const initial = useMessageStore.getState();
  const heldBefore = new Set((initial.messages[channelId] ?? []).map((message) => message.id));
  const startedAt = Date.now();
  initial.setLoadingMessages(channelId, true);
  mark(read, 'load-start');
  // Dispatch before touching IndexedDB; even an unavailable cache cannot delay HTTP.
  const response = api.fetchMessages(channelId, {
    limit: 100, ...(read.resync ? { includeDeleted: true } : {}), signal: read.controller.signal,
  });
  hydrateFromCache(read);
  try {
    const page = await response;
    if (!current(read)) return [];
    read.head.abort();
    if (page.viewer_id !== undefined && page.viewer_id !== userId) {
      rejectMessageViewer(userId ?? '', page.viewer_id);
      return [];
    }
    const verified = typeof page.viewer_id === 'string' && page.viewer_id === userId;
    if (!verified) { read.networkOnly = true; warn('missing-viewer'); discardPreview(read); }
    if (read.receipts.overflowed) discardPreview(read);
    const result = reconcileMessageWindow(page.messages, read.receipts.overflowed ? [] : read.receipts.receipts);
    const state = useMessageStore.getState();
    const existing = state.messages[channelId] ?? [];
    const incomingIds = new Set(result.messages.map((message) => message.id));
    const floor = page.messages.reduce<Message | undefined>((min, message) => !min || message.created_at < min.created_at ||
      (message.created_at === min.created_at && message.id < min.id) ? message : min, undefined);
    const rangesMeet = page.messages.some((message) => read.held.has(message.id));
    const older = (read.resync || read.correction) && page.has_more && rangesMeet
      ? existing.filter((message) => !incomingIds.has(message.id) && (floor !== undefined && (message.created_at < floor.created_at || (message.created_at === floor.created_at && message.id < floor.id))) && !read.preview.has(message.id)) : [];
    const live = existing.filter((message) => !incomingIds.has(message.id) && !read.preview.has(message.id) &&
      (read.arrivals.has(message.id) || (read.receipts.overflowed && !message.deleted_at && message.created_at > startedAt)));
    const rows = [...new Map([...older, ...result.messages, ...live].map((message) => [message.id, message])).values()]
      .sort((a, b) => a.created_at - b.created_at || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    useMessageStore.setState({
      messages: { ...state.messages, [channelId]: rows },
      hasMore: { ...state.hasMore, [channelId]: older.length ? state.hasMore[channelId] : page.has_more },
    });
    read.preview.clear();
    publications.set(channelId, { rows, read, kind: 'server' });
    if (verified && bound?.enabled && !read.networkOnly && !result.ambiguous) {
      seedWindow(read, result.canonicalMessages, page.has_more, Date.now());
    } else if (result.ambiguous || read.receipts.overflowed) {
      evict(read); correction(read);
    }
    return page.messages.filter((message) => !heldBefore.has(message.id));
  } catch (error) {
    if (!current(read) || read.controller.signal.aborted) return [];
    if (read.overflowed) discardPreview(read);
    if (error instanceof api.ApiError && error.status === 401) { endMessageCacheSession(); return []; }
    if (error instanceof api.ApiError && (error.status === 403 || error.status === 404)) {
      discardPreview(read); evict(read);
      useMessageStore.getState().purgeChannels([channelId]);
      if (error.status === 404) return [];
    }
    throw error;
  } finally {
    read.pending = false;
    read.head.abort();
    if (current(read)) useMessageStore.getState().setLoadingMessages(channelId, false);
  }
};

if (typeof window !== 'undefined') window.addEventListener('pagehide', () => {
  for (const entry of canonical.values()) if (entry.timer) flush(entry);
});
